import ButtonBase from '@material-ui/core/ButtonBase';
import generateReportFromView from '@opsbase/lib-data-view/helpers/generateReportFromView';
import isViewMandatory from '@opsbase/lib-data-view/helpers/isViewMandatory';
import DataType from '@opsbase/lib-datatype/DataType';
import doNothing from '@opsbase/lib-general/utils/doNothing';
import { getFromStringOrFunction } from '@opsbase/lib-general/utils/getFromStringOrFunction';
import i18n from '@opsbase/lib-i18n';
import Pages from '@opsbase/lib-pages';
import Palette from '@opsbase/lib-palette';
import useSelf from '@opsbase/react-ctrl-self/useSelf';
import useReportUpdate from '@opsbase/react-gql-hooks/report/useReportUpdate';
import useSelfUpdate from '@opsbase/react-gql-hooks/self/useSelfUpdate';
import useCss from '@opsbase/web-css';
import useViewRegistryActions from '@opsbase/web-ctrl-view/useViewRegistryActions';
import DeleteIcon from '@opsbase/web-icons/icons/DeleteIcon';
import EditIcon from '@opsbase/web-icons/icons/EditIcon';
import FavIcon from '@opsbase/web-icons/icons/FavIcon';
import ShareIcon from '@opsbase/web-icons/icons/ShareIcon';
import Horizontal from '@opsbase/web-layout/Horizontal';
import useNav from '@opsbase/web-nav/useNav';
import useParams from '@opsbase/web-nav/useParams';
import useNotificationActions from '@opsbase/web-notification/useNotificationActions';
import TextFieldWithStateAndBlur from '@opsbase/web-textfield/TextFieldWithStateAndBlur';
import Button from '@opsbase/web-ui/Button';
import IconButton from '@opsbase/web-ui/IconButton';
import Text from '@opsbase/web-ui/Text';
import React, { useCallback, useState } from 'react';
const makeMyClasses = (isDirty, isFavourite, isSelected, isShared, idx) => ({
    clickabletext: {
        width: '100%',
        minHeight: 32,
        justifyContent: 'left',
        cursor: 'pointer',
        transition: 'color 0.1s, border-color 0.3s',
        color: isSelected ? Palette.primary.main : undefined,
    },
    bigbutton: {
        opacity: isDirty ? 1 : 0,
        pointerEvents: isDirty ? 'unset' : 'none',
        position: 'absolute',
        top: 4,
        bottom: 4,
        width: 45,
        padding: 0,
        minWidth: 45,
        fontSize: 12,
    },
    savebutton: {
        right: 50,
    },
    resetbutton: {
        right: 0,
    },
    root: {
        left: 0,
        right: 0,
        minHeight: 36,
        position: 'absolute',
        padding: '0 8px',
        top: 8 + idx * 40,
        transition: 'top 0.3s, background-color 0.3s, border-color 0.3s, border-radius 0.3s',
        borderRadius: 0,
        borderLeft: '2px solid transparent',
        borderLeftColor: isSelected ? Palette.primary.main : 'transparent',
        '& .MuiFormControl-root': {
            margin: 0,
        },
        '& input': {
            padding: '6px !important',
        },
        '&:hover': {
            borderRadius: 16,
            backgroundColor: Palette.LightBlue.main,
        },
        '& .button': {
            '& .MuiSvgIcon-root': {
                transition: 'opacity 0.3s',
            },
            pointerEvents: 'none',
            backgroundColor: Palette.White.main,
            borderRadius: 12,
            position: 'absolute',
            transition: 'right 0.3s',
        },
        '&:hover .button': {
            pointerEvents: 'unset',
        },
        '& .edit': {
            '& .MuiSvgIcon-root': {
                opacity: 0,
            },
            right: 0,
        },
        '& .delete': {
            '& .MuiSvgIcon-root': {
                opacity: 0,
            },
            right: 0,
        },
        '& .share': {
            '& .MuiSvgIcon-root': {
                opacity: isShared ? 0.6 : 0,
            },
            right: isShared ? 26 : 0,
        },
        '& .fav': {
            '& .MuiSvgIcon-root': {
                opacity: isFavourite ? 0.6 : 0.3,
            },
            right: 0,
        },
        '&:hover .edit': {
            '& .MuiSvgIcon-root': {
                opacity: 0.3,
                '&:hover': {
                    opacity: 1,
                },
            },
            right: 78,
        },
        '&:hover .delete': {
            '& .MuiSvgIcon-root': {
                opacity: 0.3,
                '&:hover': {
                    opacity: 1,
                },
            },
            right: 52,
        },
        '&:hover .share': {
            '& .MuiSvgIcon-root': {
                opacity: isShared ? 0.6 : 0.3,
                '&:hover': {
                    opacity: 1,
                },
            },
            right: 26,
        },
        '&:hover .fav': {
            '& .MuiSvgIcon-root': {
                opacity: isFavourite ? 0.6 : 0.3,
                '&:hover': {
                    opacity: 1,
                },
            },
            right: 0,
        },
    },
});
export function ReportsListItem(props) {
    const { viewId } = useParams();
    const { lmss: view, idx } = props;
    const { putView } = useViewRegistryActions();
    const { goTo } = useNav();
    const updateSelf = useSelfUpdate();
    const updateReport = useReportUpdate();
    const { raise } = useNotificationActions();
    const [editable, setEditable] = useState(false);
    const self = useSelf();
    const isDirty = view.isDirty;
    const viewIsMandatory = isViewMandatory(view, self);
    const viewIsFavourite = self.preferences.isFavourite(view.id);
    const isOwned = view.isOwned;
    const isShared = view.isShared;
    const isSelected = view.id === viewId;
    const isDirtyAndOwned = isDirty && isOwned;
    const myCss = useCss(makeMyClasses, [isDirty, viewIsFavourite, isSelected, isShared, idx]);
    const handleToggleFavourite = useCallback(e => {
        e.stopPropagation();
        self.preferences.setFavourite(view.id, !viewIsFavourite);
        updateSelf({ preference: self.preferences }).catch(doNothing);
        putView(view);
    }, [self.preferences, view, viewIsFavourite, updateSelf, putView]);
    const handleBlurTitle = useCallback(newTitle => {
        view.title = newTitle;
        updateReport({ id: view.id, patch: { title: newTitle } }).catch(doNothing);
        putView(view);
        setEditable(false);
    }, [view, putView, updateReport]);
    const handleDelete = useCallback(e => {
        e.stopPropagation();
        goTo(Pages.REPORT_DELETE, { viewId: view.id });
    }, [goTo, view.id]);
    const handleShare = useCallback(e => {
        e.stopPropagation();
        view.isShared = !isShared;
        updateReport({ id: view.id, patch: { isShared: !isShared } }).catch(doNothing);
        putView(view);
    }, [isShared, view, putView, updateReport]);
    const handleSave = useCallback(e => {
        e.stopPropagation();
        raise(updateReport({ id: view.id, patch: generateReportFromView(view) }), i18n.t('savedview-save-successful')).catch(doNothing);
    }, [view, raise, updateReport]);
    const handleReset = useCallback(e => {
        e.stopPropagation();
        putView(view.cleanLmss);
    }, [view.cleanLmss, putView]);
    const handleToggleEdit = useCallback(e => {
        e.stopPropagation();
        setEditable(!editable);
    }, [editable]);
    const handleOpen = useCallback(e => {
        e.stopPropagation();
        let page;
        if (view.type === DataType.OP)
            page = Pages.OPS_LOG;
        if (view.type === DataType.ISSUE)
            page = Pages.ISSUES_LOG;
        if (view.type === DataType.OPDEFINITION)
            page = Pages.OP_DEFINITIONS;
        goTo(page, { viewId: view.id });
    }, [goTo, view.id, view.type]);
    let title = getFromStringOrFunction(view.title);
    title = (title === null || title === void 0 ? void 0 : title.length) > 28 ? title.substr(0, 28) + '...' : title;
    const textbit = isOwned && editable ? (React.createElement(TextFieldWithStateAndBlur, { white: true, autoFocus: true, alwaysBlur: true, value: view.title, onBlur: handleBlurTitle })) : (React.createElement(ButtonBase, { classes: { root: myCss.clickabletext }, component: "div", centerRipple: true, onClick: isSelected && !editable ? undefined : handleOpen },
        React.createElement(Text, { withEllipsis: true }, title)));
    return (React.createElement(Horizontal, { centered: true, classes: { root: myCss.root } },
        textbit,
        isDirtyAndOwned && (React.createElement(Button, { classes: { root: myCss.savebutton + ' ' + myCss.bigbutton }, color: Palette.primary, label: i18n.t('save'), onClick: handleSave })),
        isDirty && (React.createElement(Button, { classes: { root: myCss.resetbutton + ' ' + myCss.bigbutton }, color: Palette.GreyBlue, label: i18n.t('reset'), onClick: handleReset })),
        !isDirtyAndOwned && (React.createElement(IconButton, { noOpacity: true, classes: { root: 'edit button' }, style: { visibility: !isOwned ? 'hidden' : undefined }, disabled: !isOwned, icon: React.createElement(EditIcon, { size: 18 }), onClick: handleToggleEdit })),
        !isDirtyAndOwned && (React.createElement(IconButton, { noOpacity: true, classes: { root: 'delete button' }, style: { visibility: !isOwned ? 'hidden' : undefined }, icon: React.createElement(DeleteIcon, { size: 18 }), onClick: handleDelete })),
        !isDirtyAndOwned && (React.createElement(IconButton, { noOpacity: true, classes: { root: 'share button' }, style: { visibility: !isOwned ? 'hidden' : undefined }, icon: React.createElement(ShareIcon, { size: 18, color: isShared ? Palette.primary : undefined }), onClick: handleShare })),
        !viewIsMandatory && !isDirtyAndOwned && (React.createElement(IconButton, { noOpacity: true, classes: { root: 'fav button' }, onClick: handleToggleFavourite, icon: React.createElement(FavIcon, { size: 18, color: viewIsFavourite ? Palette.primary : undefined }) }))));
}
export default ReportsListItem;
